import React, { useCallback, useMemo, useRef, useState  } from "react";
import toast from "react-hot-toast";

import { PDFExport } from "@progress/kendo-react-pdf";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";

import Button from "../../themed/button";
import Conditional from "../../common/conditional";
import LoadingBadge from "../../themed/loading-badge";
import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";

import { Project } from "../../../utils/models";
import { DEFAULT_REPORT_TITLE } from "../../../utils/constants";
import { base64Upload } from "../../../services/fileUpload";

interface Props {
    project: Project;
    onGenerateURL: (url: string) => void;
}

const CustomCover: React.FC<Props> = ({ project, onGenerateURL }) => {
    const [isPending, setPending] = useState(false);

    const elementRef = useRef<HTMLDivElement>(null);

    const pdfOptions = useMemo(() => { 
        return {
            forcePageBreak: '.pdf-page',
            scale: 0.5,
            paperSize: 'A4',
            author: "Arridex",
            subject: project.documentNumber,
            title: project.projectTitle,
            producer: "RSMG",
            fileName: project.documentNumber + '.pdf',
        };
    }, [project]);

    const updatedProject = useMemo(() => {
        return {
            ...project,
            organisation: {
                _id: project.organisationId,
                organisationLogo: project.organisationLogo,
                organisationName: project.organisationName,
            },
            facility: {
                _id: project.facilityId,
                facilityName: project.facilityName,
            },
            reviews: [],
            report: {
                _id: project._id,
                createdAt: project.createdAt,
                creatorName: project.projectActivator,
            },
        } as unknown as Project;
    }, [project]);

    const generatePDFURL = useCallback(async () => {
        if (!elementRef.current) return null;

        setPending(true);

        try {
            const group = await drawDOM(elementRef.current, pdfOptions);
            const imageString: string = await exportPDF(group, pdfOptions);
    
            const url = await base64Upload(project.documentNumber + '.pdf', imageString);
            if (url) onGenerateURL(url);
        } catch (error) {
            toast.error('An error occurred. Could not generate the url')
        } finally {
            setPending(false);
        }
    }, [project.documentNumber, onGenerateURL, pdfOptions]);

    return ( 
        <React.Fragment>
            <PDFExport {...pdfOptions}>
                <div ref={elementRef}>
                   <CustomCoverPage reportTitle={project.inspectionTypeName} />

                    <CustomInfoSheet
                        projectDetails={updatedProject}
                        workOrderNumber=''
                        reportTitle={DEFAULT_REPORT_TITLE}
                    />
                </div>
            </PDFExport>

            <div className="d-flex align-items-center justify-content-end">
                <Conditional isVisible={!isPending}>
                    <Button label="Proceed" icon="binoculars-fill"  onClick={() => generatePDFURL()} />
                </Conditional>

                <Conditional isVisible={isPending}>
                    <LoadingBadge message="Generating cover page..." />
                </Conditional>
            </div>
        </React.Fragment>
    );
};
 
export default CustomCover;